import React, { useState } from "react";
import { api } from "@/auth/AuthContext";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Card, Button } from "@/components/ui-kit";
import { Check, Sparkle, Lightning } from "@phosphor-icons/react";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

const Upgrade = () => {
  const { user } = useAuth();
  const { t, lang } = useT();
  const [loading, setLoading] = useState(null);
  const [params] = useSearchParams();

  React.useEffect(() => {
    if (params.get("canceled")) toast.info(lang === "cs" ? "Platba zrušena" : "Payment canceled");
  }, [params, lang]);

  const checkout = async (plan) => {
    setLoading(plan);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(null);
    }
  };

  const isCurrent = (plan) => user?.plan === plan;

  const tiers = [
    {
      id: "FREE",
      price: "0",
      desc: lang === "cs" ? "Pro začátečníky" : "Get started",
      features: lang === "cs" ? [
        "3 AI analýzy denně",
        "Risk kalkulačka",
        "Základní dashboard",
      ] : [
        "3 AI analyses per day",
        "Risk calculator",
        "Basic dashboard",
      ],
    },
    {
      id: "PREMIUM",
      price: "499",
      desc: lang === "cs" ? "Pro aktivní tradery" : "For active traders",
      highlight: true,
      features: lang === "cs" ? [
        "30 AI analýz denně",
        "Trading deník + statistiky",
        "AI Mentor 24/7 (včetně obrázků)",
        "AI scoring (A+ až F)",
        "Equity křivka + Profit Factor",
      ] : [
        "30 AI analyses per day",
        "Trading journal + stats",
        "AI Mentor 24/7 (incl. images)",
        "AI scoring (A+ to F)",
        "Equity curve + Profit Factor",
      ],
    },
    {
      id: "ELITE",
      price: "1290",
      desc: lang === "cs" ? "Pro prop firm profesionály" : "For prop firm pros",
      features: lang === "cs" ? [
        "100 AI analýz denně",
        "Vše z PREMIUM",
        "Prop Firm Guardian",
        "Challenge Mode + statistiky",
        "Smart Alerts (email + push)",
        "Market Scanner + Ekonomický kalendář",
        "Prioritní AI",
      ] : [
        "100 AI analyses per day",
        "Everything in PREMIUM",
        "Prop Firm Guardian",
        "Challenge Mode + stats",
        "Smart Alerts (email + push)",
        "Market Scanner + Economic Calendar",
        "Priority AI",
      ],
    },
  ];

  return (
    <div>
      <Seo
        title="Ceník a předplatné — TradeMind AI | Slevový kód START10"
        description="TradeMind AI sleva: slevový kód START10 = 10 % na 3 měsíce. Prop trading aplikace s obchodním deníkem zdarma (FREE plán), PREMIUM 499 Kč, ELITE 1 290 Kč měsíčně."
      />
      <PageHeader
        title={lang === "cs" ? "Upgrade plánu" : "Upgrade your plan"}
        sub={lang === "cs" ? "Odemkni více AI analýz a Prop Firm Guardian" : "Unlock more AI analyses and Prop Firm Guardian"}
      />
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {tiers.map((tier) => {
            const current = isCurrent(tier.id);
            return (
              <div key={tier.id} data-testid={`upgrade-tier-${tier.id}`} className={`relative rounded-xl p-6 sm:p-8 border ${tier.highlight ? "border-blue-500/50 bg-gradient-to-b from-blue-500/5 to-zinc-950" : "border-zinc-800 bg-zinc-950"}`}>
                {tier.highlight && (
                  <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-blue-600 text-xs font-mono uppercase tracking-wider text-white">{lang === "cs" ? "Nejoblíbenější" : "Most popular"}</div>
                )}
                {current && (
                  <div className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-green-600 text-xs font-mono uppercase tracking-wider text-white">{lang === "cs" ? "Aktuální" : "Current"}</div>
                )}
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">{tier.id}</div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-semibold text-zinc-50 tracking-tight">{tier.price}</span>
                  <span className="text-base text-zinc-300">Kč</span>
                  <span className="text-sm text-zinc-500">/{lang === "cs" ? "měsíc" : "month"}</span>
                </div>
                <p className="text-sm text-zinc-400 mb-6">{tier.desc}</p>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check weight="bold" size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {tier.id === "FREE" || current ? (
                  <button disabled className="block w-full text-center px-4 py-2.5 rounded-md font-medium text-sm bg-zinc-900 text-zinc-500 cursor-not-allowed border border-zinc-800">
                    {current ? (lang === "cs" ? "Aktuální plán" : "Current plan") : (lang === "cs" ? "Zdarma napořád" : "Free forever")}
                  </button>
                ) : (
                  <button
                    onClick={() => checkout(tier.id)}
                    disabled={loading === tier.id}
                    data-testid={`upgrade-cta-${tier.id}`}
                    className={`block w-full text-center px-4 py-2.5 rounded-md font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${tier.highlight ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-zinc-900 hover:bg-zinc-800 text-zinc-100 border border-zinc-800"}`}
                  >
                    {loading === tier.id ? "..." : (
                      <>
                        <Sparkle weight="bold" size={14} />
                        {lang === "cs" ? `Upgradovat na ${tier.id}` : `Upgrade to ${tier.id}`}
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-center text-sm text-amber-400/90 mt-6 max-w-2xl mx-auto" data-testid="promo-code-note">
          {lang === "cs"
            ? <>Máš slevový kód? Zadej ho při platbě a získej 10% slevu na 3 měsíce (např. <strong className="font-mono">START10</strong> pro nové uživatele).</>
            : <>Have a discount code? Enter it at checkout for 10% off for 3 months (e.g. <strong className="font-mono">START10</strong> for new users).</>}
        </p>
        <p className="text-center text-xs text-zinc-600 mt-4 max-w-2xl mx-auto">
          {lang === "cs"
            ? "Předplatné se obnovuje měsíčně. Můžeš ho kdykoli zrušit v nastavení. Bezpečné platby přes Stripe."
            : "Subscription renews monthly. Cancel anytime in settings. Secure payments via Stripe."}
        </p>
      </div>
    </div>
  );
};

export default Upgrade;
